/**
 * Copyright (c) 2023 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useState } from 'react';
import { css, StyleSheet } from 'aphrodite';
import { Typography, Text } from '@blend/enterprise-components';
import { useSelector } from 'react-redux';
import { NotaryAPI } from '../../../../../api/notary/types';
import { State } from '../../../../../redux';
import { enterpriseColors } from '../../../../../../src/style/colors';
import { NotesListItem } from './NotesListItem';

const styles = StyleSheet.create({
  wrapper: {
    ...Typography.Roboto(),
    maxWidth: 600,
    paddingBottom: '2rem',
  },
  borderRadiusBottom: {
    borderBottomLeftRadius: 3,
    borderBottomRightRadius: 3,
  },
  borderRadiusTop: {
    borderTopLeftRadius: 3,
    borderTopRightRadius: 3,
  },
  notesListItemWrapper: {
    border: `1px solid ${enterpriseColors.NEUTRAL_200}`,
    overflow: 'hidden',
    ':hover': {
      boxShadow: `0 5px 5px 0px ${enterpriseColors.BOX_SHADOW}`,
      position: 'relative', // need this for z-index to work
      zIndex: 1, // need this to show box shadow on top of sibling
    },
    ':not(:last-child)': {
      marginBottom: -1, // prevent double borders between adjacent elements
    },
    transitionProperty: 'box-shadow',
    transitionDuration: '0.2s',
    transitionTimingFunction: 'ease',
  },
  notesListItemWrapperOpen: {
    borderRadius: 3,
    boxShadow: `0 5px 5px 0px ${enterpriseColors.BOX_SHADOW}`,
    margin: '12px auto',
    // need this override specificity of notesListItemWrapper
    ':not(:last-child)': {
      margin: '12px auto',
    },
  },
  instructionCard: {
    color: enterpriseColors.NEUTRAL_700,
    margin: 7,
    fontSize: 14,
  },
  emptyNotesList: {
    ...Typography.Roboto(),
    color: enterpriseColors.NEUTRAL_800,
    padding: '20px 20px',
    maxWidth: 600,
    border: `1px solid ${enterpriseColors.NEUTRAL_200}`,
    borderRadius: 6,
    backgroundColor: enterpriseColors.WHITE,
    alignItems: 'start',
  },
});

export const NotesList: React.FC = ({}) => {
  const [isOpenMap, setIsOpenMap] = useState<{ [key: string]: boolean }>({});
  const { roomDetail } = useSelector((state: State) => state);

  const notes = roomDetail.notes as NotaryAPI.NotesResponse[];

  const toggleIsOpen = (noteId: string) => {
    const newIsOpen = !isOpenMap[noteId];
    const newIsOpenMap = { ...isOpenMap, [noteId]: newIsOpen };
    setIsOpenMap(newIsOpenMap);
  };

  if (notes.length === 0) {
    return (
      <div id="it-emptyNotesList" className={css(styles.emptyNotesList)}>
        <div className={css(styles.instructionCard)}>
          <Text>No Notes Added</Text>
        </div>
      </div>
    );
  }

  return (
    <div id="it-NotesList" className={css(styles.wrapper)}>
      <div>
        {notes.map((note, idx) => {
          const isOpen = !!isOpenMap[note.id];
          // first child or preceding note is open
          const shouldHaveBorderRadiusTop = idx === 0 || !!isOpenMap[notes[idx - 1].id];
          // last child or following note is open
          const shouldHaveBorderRadiusBottom = idx === notes.length - 1 || !!isOpenMap[notes[idx + 1].id];
          return (
            <div
              key={note.id}
              className={css(
                styles.notesListItemWrapper,
                isOpen && styles.notesListItemWrapperOpen,
                shouldHaveBorderRadiusTop && styles.borderRadiusTop,
                shouldHaveBorderRadiusBottom && styles.borderRadiusBottom,
              )}
            >
              <NotesListItem
                noteId={note.id}
                notaryFullName={note.notaryName}
                message={note.message}
                createdAt={note.createdAt}
                isOpen={isOpen}
                toggleIsOpen={toggleIsOpen}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};
